<script setup>
import { WALKER_COMMANDS } from '@/services/helpers/ElbwalkerHelper'
import { useCustomerStore } from '@/stores/customer'
import { useFeaturesStore } from '@/stores/features'
import { elb } from '@elbwalker/walker.js'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, onMounted, ref, watchEffect } from 'vue'

const { appContext } = getCurrentInstance()
const translate = appContext.config.globalProperties.translate

const props = defineProps({
  emailRegex: RegExp,
  allowedActions: {
    type: Array,
    default: () => ['login', 'register', 'forgot-password']
  }
})

const emit = defineEmits([
  'showRegister',
  'showForgotPassword',
  'success',
  'showTwoFactor',
  'showSoftLogin',
  'showGuest'
])

const customerStore = useCustomerStore()
const featuresStore = useFeaturesStore()
const { customerProfile, customerPresets, softLoggedIn, customerActive } =
  storeToRefs(customerStore)

const email = ref(customerPresets.value.email)
const password = ref('')
const showPassword = ref(false)
const loading = ref(false)
const form = ref(null)
const passwordInput = ref(null)

const canRegister = computed(
  () => props.allowedActions.includes('register') && !softLoggedIn.value
)
const canForgotPassword = computed(() =>
  props.allowedActions.includes('forgot-password')
)
const canGuest = computed(
  () =>
    props.allowedActions.includes('guest') &&
    !softLoggedIn.value &&
    !customerActive.value &&
    featuresStore.isFeatureEnabled('checkout_v2')
)

const rules = {
  required: (value) => !!value || translate('validation_msg_required'),
  minLength: (value) =>
    (value?.length ?? 0) >= 8 || translate('validation_msg_min', 8),
  email: (value) =>
    props.emailRegex.test(value) || translate('validation_msg_email')
}

const el = ref(null)

onMounted(() => {
  elb(WALKER_COMMANDS.init, el.value)
})

const focusPassword = () => {
  passwordInput.value?.focus()
}

const customerEmail = computed(() => {
  return softLoggedIn.value ? (customerProfile.value?.email ?? '') : email.value
})

const validateForm = async () => {
  const formInstance = form.value
  if (!formInstance) return false
  const validationResult = await formInstance.validate()
  return validationResult.valid
}

const loginAction = async () => {
  if (!(await validateForm())) return

  loading.value = true

  const loginEmail = softLoggedIn.value ? customerEmail.value : email.value

  try {
    await customerStore.login({
      email: loginEmail,
      password: password.value
    })
    emit('success')
  } catch ({ cause }) {
    if (cause === 1014) {
      emit('showTwoFactor')
    }
  } finally {
    loading.value = false
  }
}

watchEffect(() => {
  if (softLoggedIn.value) {
    emit('showSoftLogin')
  }
})
</script>

<template>
  <div
    ref="el"
    class="d-flex flex-column"
    data-elb="account"
    data-elb-account="action:login"
    data-elbaction="visible"
  >
    <span v-if="!softLoggedIn" class="text-center">
      {{ translate('auth:enter_mail_and_password') }}
    </span>
    <span
      v-if="softLoggedIn"
      class="text-center mt-3"
      v-html="translate('checkout:forgot_pw', customerEmail)"
    />
    <VForm
      ref="form"
      lazy-validation
      class="d-flex flex-column justify-center text-start mt-5"
      @submit.prevent="loginAction"
    >
      <VTextField
        v-if="!softLoggedIn"
        type="email"
        v-model="email"
        :rules="[rules.required, rules.email]"
        :label="translate('fe_app_label_email')"
        :disabled="!!customerPresets.email"
        autocomplete="email"
        @keydown.enter="focusPassword"
      />

      <VTextField
        class="mt-3"
        ref="passwordInput"
        v-model="password"
        :rules="[rules.required, rules.minLength]"
        :append-inner-icon="showPassword ? 'custom:eyeOn' : 'custom:eyeOff'"
        :type="showPassword ? 'text' : 'password'"
        :label="translate('fe_app_label_password')"
        autocomplete="current-password"
        @click:append-inner="showPassword = !showPassword"
        @keydown.enter="loginAction"
      />

      <VBtn
        v-if="canForgotPassword"
        variant="plain"
        size="small"
        class="align-self-start my-3 px-1"
        @click="emit('showForgotPassword')"
      >
        {{ translate('password_forgotten') }}
      </VBtn>

      <VBtn
        type="submit"
        color="primary"
        class="mt-3"
        :loading="loading"
        :disabled="loading"
        depressed
        block
      >
        {{ translate('login') }}
      </VBtn>

      <template v-if="canRegister">
        <span class="mt-5 text-center">
          {{ translate('auth_or_go_to_register') }}
        </span>
        <VBtnSecondary @click="emit('showRegister')" class="my-5">
          {{ translate('header_navigation_register') }}
        </VBtnSecondary>
      </template>
      <div
        :class="['d-flex flex-column guest-order-section', { show: canGuest }]"
      >
        <div
          class="registered-customer-advantages d-flex flex-column align-center"
        >
          <span class="font-weight-bold text-primary text-uppercase pb-2">{{
            translate('checkout:advantages')
          }}</span>
          <div class="d-flex flex-column align-start">
            <div class="d-flex flex-row pb-2">
              <VIcon color="primary">custom:checkCircle</VIcon>
              <span class="ml-2">{{ translate('checkout:advantages_1') }}</span>
            </div>
            <div class="d-flex flex-row pb-2">
              <VIcon color="primary">custom:checkCircle</VIcon>
              <span class="ml-2">{{ translate('checkout:advantages_2') }}</span>
            </div>
            <div class="d-flex flex-row">
              <VIcon color="primary">custom:checkCircle</VIcon>
              <span class="ml-2">{{ translate('checkout:advantages_3') }}</span>
            </div>
          </div>
        </div>
        <VBtn
          @click="emit('showGuest')"
          variant="tonal"
          class="guest-checkout-btn mt-5"
          color="gray"
        >
          {{ translate('order_as_guest') }}
        </VBtn>
      </div>
    </VForm>
  </div>
</template>

<style scoped>
.guest-order-section {
  display: none !important;
}

.guest-order-section.show {
  display: flex !important;
}
</style>
